<script setup>
import { computed, onMounted, reactive, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import { useMapGetter, useStore } from 'dashboard/composables/store';
import { uploadFile } from 'dashboard/helper/uploadHelper';
import { MESSAGE_VARIABLES } from 'shared/constants/messages';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import WootMessageEditor from 'dashboard/components/widgets/WootWriter/Editor.vue';
import EmailMarketingTemplatePreview from './EmailMarketingTemplatePreview.vue';
import LiquidVariableAutocomplete from './LiquidVariableAutocomplete.vue';
import LiquidVariablePicker from './LiquidVariablePicker.vue';

const props = defineProps({
  template: { type: Object, default: null },
  isSaving: { type: Boolean, default: false },
  error: { type: String, default: '' },
});

const emit = defineEmits(['save', 'cancel']);

const { t } = useI18n();
const store = useStore();
const contactAttributes = useMapGetter('attributes/getContactAttributes');

const emptyTemplate = () => ({
  name: '',
  template_type: 'email',
  status: 'active',
  sender_name: '',
  subject: '',
  message: '',
  html_content: '',
  signature: '',
  logo_url: '',
  logo_signed_id: '',
  remove_logo: false,
  button_text: '',
  button_url: '',
  utm_source: '',
  utm_medium: '',
  utm_campaign: '',
  utm_term: '',
  utm_content: '',
});

const form = reactive(emptyTemplate());
const variableTarget = ref('message');
const validationError = ref('');
const initialFormSnapshot = ref('');
const logoFileInput = ref(null);
const isUploadingLogo = ref(false);
const logoUploadError = ref('');
const hadAttachedLogo = ref(false);

const isEditing = computed(() => !!props.template?.id);
const isEmailTemplate = computed(() => form.template_type === 'email');

const templateTypes = computed(() => [
  { value: 'email', label: t('CAMPAIGN.EMAIL.TEMPLATES.TYPE.EMAIL') },
  { value: 'message', label: t('CAMPAIGN.EMAIL.TEMPLATES.TYPE.MESSAGE') },
]);

const statuses = computed(() => [
  { value: 'active', label: t('CAMPAIGN.EMAIL.TEMPLATES.STATUS.ACTIVE') },
  { value: 'archived', label: t('CAMPAIGN.EMAIL.TEMPLATES.STATUS.ARCHIVED') },
]);

const variableKeys = new Set([
  'contact.id',
  'contact.name',
  'contact.first_name',
  'contact.last_name',
  'contact.email',
  'contact.phone',
  'agent.name',
  'agent.first_name',
  'agent.last_name',
  'agent.email',
  'inbox.name',
  'inbox.id',
]);

const standardVariableLabels = computed(() => ({
  'contact.id': t('CAMPAIGN.EMAIL.CREATE.FORM.VARIABLES.STANDARD.CONTACT_ID'),
  'contact.name': t(
    'CAMPAIGN.EMAIL.CREATE.FORM.VARIABLES.STANDARD.CONTACT_NAME'
  ),
  'contact.first_name': t(
    'CAMPAIGN.EMAIL.CREATE.FORM.VARIABLES.STANDARD.CONTACT_FIRST_NAME'
  ),
  'contact.last_name': t(
    'CAMPAIGN.EMAIL.CREATE.FORM.VARIABLES.STANDARD.CONTACT_LAST_NAME'
  ),
  'contact.email': t(
    'CAMPAIGN.EMAIL.CREATE.FORM.VARIABLES.STANDARD.CONTACT_EMAIL'
  ),
  'contact.phone': t(
    'CAMPAIGN.EMAIL.CREATE.FORM.VARIABLES.STANDARD.CONTACT_PHONE'
  ),
  'agent.name': t('CAMPAIGN.EMAIL.CREATE.FORM.VARIABLES.STANDARD.AGENT_NAME'),
  'agent.first_name': t(
    'CAMPAIGN.EMAIL.CREATE.FORM.VARIABLES.STANDARD.AGENT_FIRST_NAME'
  ),
  'agent.last_name': t(
    'CAMPAIGN.EMAIL.CREATE.FORM.VARIABLES.STANDARD.AGENT_LAST_NAME'
  ),
  'agent.email': t('CAMPAIGN.EMAIL.CREATE.FORM.VARIABLES.STANDARD.AGENT_EMAIL'),
  'inbox.name': t('CAMPAIGN.EMAIL.CREATE.FORM.VARIABLES.STANDARD.INBOX_NAME'),
  'inbox.id': t('CAMPAIGN.EMAIL.CREATE.FORM.VARIABLES.STANDARD.INBOX_ID'),
  'account.name': t(
    'CAMPAIGN.EMAIL.CREATE.FORM.VARIABLES.STANDARD.ACCOUNT_NAME'
  ),
}));

const liquidVariableOptions = computed(() => {
  const standard = MESSAGE_VARIABLES.filter(variable =>
    variableKeys.has(variable.key)
  ).map(variable => ({
    label: standardVariableLabels.value[variable.key] || variable.label,
    token: `{{${variable.key}}}`,
  }));
  standard.push({
    label: standardVariableLabels.value['account.name'],
    token: '{{account.name}}',
  });

  const custom = (contactAttributes.value || []).map(attribute => {
    const key = attribute.attributeKey ?? attribute.attribute_key;
    const label =
      attribute.attributeDisplayName ?? attribute.attribute_display_name ?? key;
    const escapedKey = String(key)
      .replaceAll('\\', '\\\\')
      .replaceAll('"', '\\"');
    return {
      label,
      token: `{{contact.custom_attribute["${escapedKey}"]}}`,
    };
  });

  return [...standard, ...custom];
});

const editorAdditionalVariables = computed(() => [
  {
    key: 'account.name',
    label: standardVariableLabels.value['account.name'],
  },
]);

const utmFields = [
  ['utm_source', 'source'],
  ['utm_medium', 'medium'],
  ['utm_campaign', 'campaign'],
  ['utm_term', 'term'],
  ['utm_content', 'content'],
];

const utmValues = computed(() =>
  Object.fromEntries(
    utmFields
      .map(([formKey, metadataKey]) => [metadataKey, form[formKey].trim()])
      .filter(([, value]) => value)
  )
);

const hasUtmValues = computed(() => Object.keys(utmValues.value).length > 0);

const utmPreview = computed(() => {
  if (!hasUtmValues.value) return '';

  try {
    const url = new URL(form.button_url || 'https://exemplo.com/oferta');
    Object.entries(utmValues.value).forEach(([key, value]) => {
      url.searchParams.set(`utm_${key}`, value);
    });
    return url.toString();
  } catch {
    return '';
  }
});

const formSnapshot = () =>
  JSON.stringify(
    Object.fromEntries(
      Object.keys(emptyTemplate()).map(key => [key, form[key] ?? ''])
    )
  );

const isDirty = computed(() => formSnapshot() !== initialFormSnapshot.value);

const resetForm = template => {
  const values = emptyTemplate();
  Object.keys(values).forEach(key => {
    const metadataKey = utmFields.find(([formKey]) => formKey === key)?.[1];
    form[key] = metadataKey
      ? (template?.metadata?.utm?.[metadataKey] ?? values[key])
      : (template?.[key] ?? values[key]);
  });
  validationError.value = '';
  logoUploadError.value = '';
  hadAttachedLogo.value = !!template?.logo_attached;
  variableTarget.value = 'message';
  initialFormSnapshot.value = formSnapshot();
};

const insertVariable = token => {
  const currentValue = form[variableTarget.value] || '';
  const separator = currentValue && !/\s$/.test(currentValue) ? ' ' : '';
  form[variableTarget.value] = `${currentValue}${separator}${token}`;
};

const submit = () => {
  if (!form.name.trim()) {
    validationError.value = t('CAMPAIGN.EMAIL.TEMPLATES.FORM.NAME_REQUIRED');
    return;
  }
  if (!form.message.trim()) {
    validationError.value = t('CAMPAIGN.EMAIL.TEMPLATES.FORM.MESSAGE_REQUIRED');
    return;
  }
  if (form.template_type === 'email' && !form.subject.trim()) {
    validationError.value = t('CAMPAIGN.EMAIL.TEMPLATES.FORM.SUBJECT_REQUIRED');
    return;
  }
  if (
    isEmailTemplate.value &&
    !!form.button_text.trim() !== !!form.button_url.trim()
  ) {
    validationError.value = t(
      'CAMPAIGN.EMAIL.TEMPLATES.FORM.BUTTON_PAIR_REQUIRED'
    );
    return;
  }
  if (
    isEmailTemplate.value &&
    hasUtmValues.value &&
    (!form.utm_source.trim() ||
      !form.utm_medium.trim() ||
      !form.utm_campaign.trim())
  ) {
    validationError.value = t('CAMPAIGN.EMAIL.TEMPLATES.FORM.UTM_REQUIRED');
    return;
  }

  validationError.value = '';
  const values = Object.fromEntries(
    Object.entries(form).map(([key, value]) => [
      key,
      typeof value === 'string' ? value.trim() : value,
    ])
  );
  utmFields.forEach(([formKey]) => delete values[formKey]);
  const logoSignedId = values.logo_signed_id;
  delete values.logo_signed_id;
  if (logoSignedId) {
    values.logo = logoSignedId;
    values.logo_url = '';
  }
  const metadata = { ...(props.template?.metadata || {}) };
  if (hasUtmValues.value) metadata.utm = utmValues.value;
  else delete metadata.utm;
  values.metadata = metadata;
  if (values.template_type === 'message') {
    delete values.logo;
    values.remove_logo = hadAttachedLogo.value || values.remove_logo;
    [
      'sender_name',
      'subject',
      'html_content',
      'signature',
      'logo_url',
      'button_text',
      'button_url',
    ].forEach(key => {
      values[key] = '';
    });
    values.metadata = {};
  }
  emit('save', values);
};

const openLogoFilePicker = () => logoFileInput.value?.click();

const handleExternalLogoInput = () => {
  form.logo_signed_id = '';
  form.remove_logo = hadAttachedLogo.value;
};

const uploadLogo = async event => {
  const file = event.target.files?.[0];
  event.target.value = '';
  if (!file) return;

  if (!file.type.startsWith('image/') || file.size > 4 * 1024 * 1024) {
    logoUploadError.value = t(
      'CAMPAIGN.EMAIL.TEMPLATES.FORM.LOGO_UPLOAD_INVALID'
    );
    return;
  }

  isUploadingLogo.value = true;
  logoUploadError.value = '';
  try {
    const { fileUrl, blobId } = await uploadFile(file);
    if (!fileUrl) throw new Error('Missing uploaded file URL');
    form.logo_url = fileUrl;
    form.logo_signed_id = blobId;
    form.remove_logo = false;
  } catch {
    logoUploadError.value = t(
      'CAMPAIGN.EMAIL.TEMPLATES.FORM.LOGO_UPLOAD_ERROR'
    );
  } finally {
    isUploadingLogo.value = false;
  }
};

watch(
  () => props.template,
  template => resetForm(template),
  { immediate: true }
);

onMounted(() => store.dispatch('attributes/get'));

watch(
  () => form.template_type,
  templateType => {
    if (templateType === 'message') variableTarget.value = 'message';
  }
);

defineExpose({ isDirty });
</script>

<template>
  <form
    class="min-h-0 flex-1 overflow-y-auto md:grid md:grid-cols-[minmax(24rem,1fr)_minmax(20rem,0.9fr)] md:overflow-hidden"
    @submit.prevent="submit"
  >
    <div class="px-5 py-5 sm:px-6 md:min-h-0 md:overflow-y-auto">
      <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Input
          v-model="form.name"
          class="sm:col-span-2"
          :label="t('CAMPAIGN.EMAIL.TEMPLATES.FORM.NAME')"
          :placeholder="t('CAMPAIGN.EMAIL.TEMPLATES.FORM.NAME_PLACEHOLDER')"
          autofocus
        />

        <fieldset class="min-w-0">
          <legend class="mb-1 text-sm font-medium text-n-slate-12">
            {{ t('CAMPAIGN.EMAIL.TEMPLATES.FORM.TYPE') }}
          </legend>
          <div
            class="grid grid-cols-2 rounded-lg border border-n-weak bg-n-alpha-2 p-1"
          >
            <button
              v-for="item in templateTypes"
              :key="item.value"
              type="button"
              class="h-8 rounded-md px-2 text-sm text-n-slate-11"
              :class="{
                'bg-n-solid-2 font-medium text-n-slate-12 shadow-sm':
                  form.template_type === item.value,
              }"
              :aria-pressed="form.template_type === item.value"
              @click="form.template_type = item.value"
            >
              {{ item.label }}
            </button>
          </div>
        </fieldset>

        <fieldset class="min-w-0">
          <legend class="mb-1 text-sm font-medium text-n-slate-12">
            {{ t('CAMPAIGN.EMAIL.TEMPLATES.FORM.STATUS') }}
          </legend>
          <div
            class="grid grid-cols-2 rounded-lg border border-n-weak bg-n-alpha-2 p-1"
          >
            <button
              v-for="item in statuses"
              :key="item.value"
              type="button"
              class="h-8 rounded-md px-2 text-sm text-n-slate-11"
              :class="{
                'bg-n-solid-2 font-medium text-n-slate-12 shadow-sm':
                  form.status === item.value,
              }"
              :aria-pressed="form.status === item.value"
              @click="form.status = item.value"
            >
              {{ item.label }}
            </button>
          </div>
        </fieldset>

        <Input
          v-if="isEmailTemplate"
          v-model="form.sender_name"
          :label="t('CAMPAIGN.EMAIL.TEMPLATES.FORM.SENDER')"
          :placeholder="t('CAMPAIGN.EMAIL.TEMPLATES.FORM.SENDER_PLACEHOLDER')"
        />
        <LiquidVariableAutocomplete
          v-if="isEmailTemplate"
          v-model="form.subject"
          :options="liquidVariableOptions"
          :label="t('CAMPAIGN.EMAIL.TEMPLATES.FORM.SUBJECT')"
          :placeholder="t('CAMPAIGN.EMAIL.TEMPLATES.FORM.SUBJECT_PLACEHOLDER')"
        />

        <div class="flex flex-col gap-1 sm:col-span-2">
          <label
            id="email-marketing-template-message-label"
            class="mb-0.5 text-sm font-medium text-n-slate-12"
          >
            {{ t('CAMPAIGN.EMAIL.TEMPLATES.FORM.MESSAGE') }}
          </label>
          <div
            class="message-editor rounded-lg bg-n-alpha-black2 px-3 py-3 outline outline-1 outline-n-weak hover:outline-n-slate-6 [&_.ProseMirror-menubar]:flex [&_.ProseMirror-menubar]:flex-wrap [&_.ProseMirror-menubar]:gap-3 [&_.ProseMirror-menubar]:border-b [&_.ProseMirror-menubar]:border-n-weak [&_.ProseMirror-menubar]:pb-2 [&_.ProseMirror-menuitem]:m-0 [&_.ProseMirror-woot-style]:min-h-36 [&_.ProseMirror-woot-style]:max-h-80"
          >
            <WootMessageEditor
              v-model="form.message"
              editor-id="email-marketing-template-message"
              :aria-label="t('CAMPAIGN.EMAIL.TEMPLATES.FORM.MESSAGE')"
              channel-type="Context::Default"
              enable-variables
              :additional-template-variables="editorAdditionalVariables"
              :placeholder="
                t('CAMPAIGN.EMAIL.TEMPLATES.FORM.MESSAGE_PLACEHOLDER')
              "
              :focus-on-mount="false"
            />
          </div>
          <span class="text-xs text-n-slate-10">
            {{ t('CAMPAIGN.EMAIL.TEMPLATES.FORM.MESSAGE_HELP') }}
          </span>
        </div>

        <TextArea
          v-if="isEmailTemplate"
          v-model="form.html_content"
          class="sm:col-span-2"
          :label="t('CAMPAIGN.EMAIL.TEMPLATES.FORM.HTML')"
          :placeholder="t('CAMPAIGN.EMAIL.TEMPLATES.FORM.HTML_PLACEHOLDER')"
          resize
          custom-text-area-class="min-h-40 font-mono text-xs"
        />

        <TextArea
          v-if="isEmailTemplate"
          v-model="form.signature"
          class="sm:col-span-2"
          :label="t('CAMPAIGN.EMAIL.TEMPLATES.FORM.SIGNATURE')"
          :placeholder="
            t('CAMPAIGN.EMAIL.TEMPLATES.FORM.SIGNATURE_PLACEHOLDER')
          "
          resize
        />
      </div>

      <LiquidVariablePicker
        v-model:target="variableTarget"
        :template-type="form.template_type"
        @insert="insertVariable"
      />

      <div
        v-if="isEmailTemplate"
        class="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2"
      >
        <div class="flex flex-col gap-2 sm:col-span-2">
          <Input
            v-model="form.logo_url"
            type="url"
            :label="t('CAMPAIGN.EMAIL.TEMPLATES.FORM.LOGO_URL')"
            placeholder="https://"
            @input="handleExternalLogoInput"
          />
          <input
            ref="logoFileInput"
            type="file"
            accept="image/png,image/jpeg,image/gif,image/webp"
            class="hidden"
            @change="uploadLogo"
          />
          <div class="flex flex-wrap items-center gap-2">
            <Button
              type="button"
              size="sm"
              variant="faded"
              color="slate"
              icon="i-lucide-upload"
              :is-loading="isUploadingLogo"
              :disabled="isUploadingLogo"
              :label="t('CAMPAIGN.EMAIL.TEMPLATES.FORM.LOGO_UPLOAD')"
              @click="openLogoFilePicker"
            />
            <Button
              v-if="form.logo_url"
              type="button"
              size="sm"
              variant="ghost"
              color="slate"
              icon="i-lucide-x"
              :label="t('CAMPAIGN.EMAIL.TEMPLATES.FORM.LOGO_REMOVE')"
              @click="
                form.logo_url = '';
                form.logo_signed_id = '';
                form.remove_logo = true;
              "
            />
          </div>
          <p class="text-xs text-n-slate-10">
            {{ t('CAMPAIGN.EMAIL.TEMPLATES.FORM.LOGO_UPLOAD_HELP') }}
          </p>
          <p v-if="logoUploadError" class="text-xs text-n-ruby-10">
            {{ logoUploadError }}
          </p>
        </div>
        <Input
          v-model="form.button_text"
          :label="t('CAMPAIGN.EMAIL.TEMPLATES.FORM.BUTTON_TEXT')"
          :placeholder="
            t('CAMPAIGN.EMAIL.TEMPLATES.FORM.BUTTON_TEXT_PLACEHOLDER')
          "
        />
        <Input
          v-model="form.button_url"
          type="url"
          :label="t('CAMPAIGN.EMAIL.TEMPLATES.FORM.BUTTON_URL')"
          placeholder="https://"
        />

        <fieldset class="sm:col-span-2">
          <legend class="text-sm font-medium text-n-slate-12">
            {{ t('CAMPAIGN.EMAIL.TEMPLATES.FORM.UTM_TITLE') }}
          </legend>
          <p class="mt-1 text-xs leading-5 text-n-slate-10">
            {{ t('CAMPAIGN.EMAIL.TEMPLATES.FORM.UTM_HELP') }}
          </p>
          <div class="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Input
              v-model="form.utm_source"
              :label="t('CAMPAIGN.EMAIL.TEMPLATES.FORM.UTM_SOURCE')"
              placeholder="newsletter"
            />
            <Input
              v-model="form.utm_medium"
              :label="t('CAMPAIGN.EMAIL.TEMPLATES.FORM.UTM_MEDIUM')"
              placeholder="email"
            />
            <Input
              v-model="form.utm_campaign"
              :label="t('CAMPAIGN.EMAIL.TEMPLATES.FORM.UTM_CAMPAIGN')"
              placeholder="lancamento-julho"
            />
            <Input
              v-model="form.utm_term"
              :label="t('CAMPAIGN.EMAIL.TEMPLATES.FORM.UTM_TERM')"
              placeholder="opcional"
            />
            <Input
              v-model="form.utm_content"
              :label="t('CAMPAIGN.EMAIL.TEMPLATES.FORM.UTM_CONTENT')"
              placeholder="botao-principal"
            />
          </div>
          <p
            v-if="utmPreview"
            class="mt-3 break-all rounded-md border border-n-weak bg-n-alpha-2 px-3 py-2 text-xs text-n-slate-10"
          >
            <strong class="text-n-slate-12">
              {{ t('CAMPAIGN.EMAIL.TEMPLATES.FORM.UTM_PREVIEW') }}
            </strong>
            {{ utmPreview }}
          </p>
        </fieldset>
      </div>

      <p
        v-if="validationError || error"
        class="mt-4 border-l-2 border-n-ruby-9 py-1 pl-3 text-sm text-n-ruby-10"
      >
        {{ validationError || error }}
      </p>

      <div
        class="mt-6 flex flex-wrap justify-end gap-3 border-t border-n-weak pt-4"
      >
        <Button
          type="button"
          variant="faded"
          color="slate"
          :label="t('CAMPAIGN.EMAIL.TEMPLATES.FORM.CANCEL')"
          @click="emit('cancel')"
        />
        <Button
          type="submit"
          icon="i-lucide-save"
          :is-loading="isSaving"
          :disabled="isSaving"
          :label="
            isEditing
              ? t('CAMPAIGN.EMAIL.TEMPLATES.FORM.UPDATE')
              : t('CAMPAIGN.EMAIL.TEMPLATES.FORM.CREATE')
          "
        />
      </div>
    </div>

    <EmailMarketingTemplatePreview
      class="min-h-[28rem] border-t border-n-weak md:min-h-0 md:border-l md:border-t-0"
      :template-data="form"
    />
  </form>
</template>
